import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { hooks, media, signaling } from '@lifesize/clients.sdk';
import personIcon from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import { disableOnboarding, hideOnboarding } from 'actions/Onboarding';
import VbbSettings from 'components/VbbSettings/VbbSettings';
import AvSettings from 'components/AvSettings/AvSettings';
import Button from 'components/Button/Button';
import PageLayout from 'layouts/PageLayout/PageLayout';
import { isChrome } from 'utils/browser-utils';
import { getDevicesByKind } from 'utils/deviceUtils';
import classes from './Onboarding.module.scss';

const chromeHelpLink = 'https://support.google.com/chrome/answer/2693767';

interface Props {}

const Onboarding = (props: Props) => {
  const dispatch = useDispatch();
  const devices = hooks.useDevices();
  const hasAudio = !!useMemo(() => getDevicesByKind(devices, 'audioinput'), [devices])?.length;
  const hasVideo = !!useMemo(() => getDevicesByKind(devices, 'videoinput'), [devices])?.length;
  const { acquiring } = hooks.useMedia();
  const isOnboarding = useSelector((state) => get(state, 'app.onboarding.isOnboarding'));
  const onboardingAvailable = useSelector((state) => !get(state, 'app.onboarding.disableOnboarding'));
  const [showVbbSettings, setShowVbbSettings] = useState(false);

  const onHideOnboarding = useCallback(() => {
    dispatch(hideOnboarding());
    dispatch(disableOnboarding());
  }, [dispatch]);

  const onToggleVbbSettings = () => {
    setShowVbbSettings(!showVbbSettings);
  };

  useEffect(() => {
    if (onboardingAvailable && isOnboarding) {
      signaling.settings.setDoNotDisturb(true);
    }

    return function componentWillUnmount() {
      signaling.settings.setDoNotDisturb(false);
      try {
        media.stopWebcam();
      } catch (ex) {
        console.log('error stopping webcam', ex);
      }
    };

    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if you aren't specifically onboarding and you have given device permissions and available devices, move ahead automatically
    if (!isOnboarding && hasAudio && hasVideo && !acquiring) {
      onHideOnboarding();
    }
  }, [isOnboarding, hasAudio, hasVideo, onHideOnboarding, acquiring]);

  const onboardingText = () => {
    // eslint-disable-line consistent-return
    if (hasVideo && hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.instructions"
              defaultMessage="Let's review the way your camera and microphone work in calls."
            />
          </div>
          <div>
            <FormattedMessage
              id="components.onboarding.secondInstructions"
              defaultMessage="You can change these preferences at any time in Settings."
            />
            <span className={classes.iconCircle}>
              <SVG src={personIcon} data-testid="personIcon" />
            </span>
          </div>
        </div>
      );
    }

    if (!hasVideo && !hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noAccess"
              defaultMessage="We do not have access to your camera or microphone."
            />
          </div>
          {allowAccessMessage()}
        </div>
      );
    }

    if (hasVideo && !hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noMicAccess"
              defaultMessage={
                'We do not have access to your microphone. ' +
                'If you proceed with these settings, you will need to call in by phone to hear the audio.'
              }
            />
          </div>
          {allowAccessMessage()}
        </div>
      );
    }

    if (!hasVideo && hasAudio) {
      return (
        <div className={classes.onboardingText}>
          <div>
            <FormattedMessage
              id="components.onboarding.noCameraAccess"
              defaultMessage="We do not have access to your camera. If you proceed with these settings, your calls will be audio only."
            />
          </div>
          {allowAccessMessage()}
        </div>
      );
    }
  };

  const allowAccessMessage = () => {
    // @ts-ignore
    if (isChrome() && !window.desktopApp) {
      return (
        <div>
          <FormattedMessage
            id="components.onboarding.allowAccess"
            defaultMessage="For the best experience, go to your Chrome settings to allow access."
          />
          <a href={chromeHelpLink} className={classes.helpLink} rel="noopener noreferrer" target="_blank">
            <FormattedMessage id="components.onboarding.accessInstructionsLink" defaultMessage="Learn more." />
          </a>
        </div>
      );
    }

    return (
      <div>
        <FormattedMessage
          id="components.onboarding.allowAccessGeneric"
          defaultMessage="For the best experience, please allow access."
        />
      </div>
    );
  };

  return (
    <PageLayout className={isOnboarding ? '' : classes.hiddenCameraCheck}>
      <div style={{ overflow: 'scroll' }}>
        <section className={classes.onboardingContainer}>
          <h1>
            <FormattedMessage id="components.onboarding.title" defaultMessage="Set my Call Preferences" />
          </h1>
          {onboardingText()}
          {isOnboarding &&
            (showVbbSettings ? (
              <VbbSettings onToggleVbbSettings={onToggleVbbSettings} />
            ) : (
              <AvSettings onToggleVbbSettings={onToggleVbbSettings} />
            ))}
        </section>
        <Button onClick={onHideOnboarding} style={{ minWidth: 304, padding: 12, width: 304, marginTop: 50 }}>
          <FormattedMessage id="components.onboarding.next" defaultMessage="Next" />
        </Button>
      </div>
    </PageLayout>
  );
};

export default Onboarding;
